import { useEffect } from "react"
import type { CertFont } from "../../../service/certificate"

// Runtime @font-face injector. Mounts a single <style id="cert-custom-fonts">
// element in document.head and rewrites it whenever the font list changes.
// The Fonts admin page uses it for the live preview column; the Designer
// uses it so a layer's font-family set to a custom face renders with the
// real typeface on the canvas. (Ported from RMS use-custom-font-faces.js.)
//
// The PDF renderer ships its own @font-face block server-side, so generated
// PDFs don't depend on any browser tab's document state.

const STYLE_ID = "cert-custom-fonts"

const buildCSS = (fonts: CertFont[]): string =>
  (fonts || [])
    .filter(f => f.isActive !== false && f.family && f.fileUrl && f.format)
    .map(
      f => `@font-face {
  font-family: '${f.family}';
  src: url('${f.fileUrl}') format('${f.format}');
  font-display: swap;
}`
    )
    .join("\n")

export function useCustomFontFaces(fonts: CertFont[]) {
  useEffect(() => {
    if (typeof document === "undefined") return
    let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null
    if (!style) {
      style = document.createElement("style")
      style.id = STYLE_ID
      document.head.appendChild(style)
    }
    style.textContent = buildCSS(fonts)
    // Don't remove on unmount — multiple pages may rely on it; the next
    // mount overwrites the contents.
  }, [fonts])
}
